import { Close, Settings } from "@mui/icons-material";
import {
  Box,
  Drawer,
  Fab,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useSearchParams } from "react-router";
import { FEATURE_GROUPS, type FeatureFlags, useFeatureFlags } from "@/contexts/FeaturesContext";

type FlagConfig = (typeof FEATURE_GROUPS)[number]["flags"][number];
type SetFlag = <K extends keyof FeatureFlags>(key: K, value: FeatureFlags[K]) => void;

interface FlagProps {
  config: FlagConfig;
  flags: FeatureFlags;
  setFlag: SetFlag;
}

function BooleanFlag({ config, flags, setFlag }: FlagProps) {
  return (
    <FormControlLabel
      labelPlacement="start"
      label={config.label}
      sx={{ display: "flex", justifyContent: "space-between", mx: 0 }}
      control={
        <Switch
          checked={flags[config.key] as boolean}
          onChange={(e) => setFlag(config.key, e.target.checked as FeatureFlags[typeof config.key])}
        />
      }
    />
  );
}

function EnumFlag({ config, flags, setFlag }: FlagProps) {
  if (!("options" in config) || !config.options) return null;

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography>{config.label}</Typography>
      <Select
        size="small"
        value={flags[config.key]}
        onChange={(e) => setFlag(config.key, e.target.value as FeatureFlags[typeof config.key])}
      >
        {config.options.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </Stack>
  );
}

function FlagRow(props: FlagProps) {
  if ("options" in props.config && props.config.options) {
    return <EnumFlag {...props} />;
  }
  return <BooleanFlag {...props} />;
}

export default function FeaturesPanel() {
  const { flags, setFlag, panelOpen, setPanelOpen } = useFeatureFlags();
  const [searchParams] = useSearchParams();
  const isTestMode = searchParams.has("test");

  return (
    <>
      {isTestMode && !panelOpen && (
        <Fab
          size="small"
          onClick={() => setPanelOpen(true)}
          sx={{
            position: "fixed",
            top: { xs: 20, sm: 33 },
            right: { xs: 120, sm: 16 },
            zIndex: 1300,
            width: 28,
            height: 28,
            minHeight: "unset",
            bgcolor: "background.paper",
            "& .MuiSvgIcon-root": { fontSize: 16 },
          }}
        >
          <Settings />
        </Fab>
      )}
      <Drawer anchor="right" open={panelOpen} onClose={() => setPanelOpen(false)}>
        <Box sx={{ width: { xs: "100vw", sm: 500 }, p: 3 }}>
          <IconButton
            size="small"
            onClick={() => setPanelOpen(false)}
            sx={{ position: "fixed", right: 10, top: 15 }}
          >
            <Close fontSize="small" />
          </IconButton>
          <Stack spacing={2}>
            {FEATURE_GROUPS.map((group) => (
              <Box key={group.title}>
                <Typography variant="h3" sx={{ mb: 2.5 }}>
                  {group.title}
                </Typography>
                <Stack spacing={2}>
                  {group.flags.map((flagConfig) => (
                    <FlagRow
                      key={flagConfig.key}
                      config={flagConfig}
                      flags={flags}
                      setFlag={setFlag}
                    />
                  ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        </Box>
      </Drawer>
    </>
  );
}
